import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import {
  Button,
  makeStyles,
  shorthands,
  tokens,
  Toaster,
  useToastController,
  Toast,
  ToastTitle, mergeClasses
} from "@fluentui/react-components";
import {
  DocumentAdd20Regular,
  DocumentBulletList20Regular,
  Briefcase20Regular,
  Building20Regular,
  Search20Regular,
  Checkmark16Regular,
} from "@fluentui/react-icons";
import { apiUrl } from "../utils/api";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { sectionColorHex } from "../components/risikoanalyseStep/sectionColor";
import BottomBar from "../components/risikoanalyseStep/BottomBar";
import StepHero from "../components/allgemeinContractStep/StepHero";
import { ALLGEMEIN_CONTRACT_SECTIONS } from "../components/allgemeinContractStep/sections";
import VertragsBasisSection from "../components/allgemeinContractStep/VertragsBasisSection";
import ZahlungSection from "../components/allgemeinContractStep/ZahlungSection";
import GefahrenMatrix from "../components/allgemeinContractStep/GefahrenMatrix";
import {
  emptyMatrixState,
  isInhaltOnly,
  isMatrixMode,
  type GefahrenMatrixState,
} from "../components/allgemeinContractStep/gefahrenMatrixData";
import { ContactSelector } from "../components/ContactSelector";
import { useSharedStyles } from "../components/allgemeinContractStep/sharedStyles";
import {
  TARIFIERUNGEN,
  VERSICHERER_OPTIONS,
  VERTRAGSARTEN,
  requiresTarifierung,
  type ContactSummary,
  type ContractStepKind,
  type RiskOption,
  type SummenInfo,
  type TarifierungKey,
  type UmfangInfo,
  type VersichererKey,
  type VertragsartKey,
  type VorvertragInfo,
  type ZahlungInfo,
} from "../components/allgemeinContractStep/types";

const DRAFT_LS_PREFIX = "allgemein-contract-draft:";
const AUTOSAVE_DEBOUNCE_MS = 1500;

const useStyles = makeStyles({
  page: {
    paddingTop: "0",
    paddingBottom: "0",
    paddingLeft: "24px",
    paddingRight: "24px",
    maxWidth: "1200px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    minHeight: "100%",
  },
  stickyHeader: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    marginLeft: "-24px",
    marginRight: "-24px",
    paddingTop: "16px",
    paddingBottom: "14px",
    paddingLeft: "24px",
    paddingRight: "24px",
    backgroundColor: "#f8f8f8",
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
    ...shorthands.borderBottom("1px", "solid", "rgba(45,20,43,0.08)"),
  },
  stickyFooter: {
    position: "sticky",
    bottom: 0,
    zIndex: 10,
    marginTop: "auto",
    marginLeft: "-24px",
    marginRight: "-24px",
    backgroundColor: BRAND.white,
    ...shorthands.borderTop("1px", "solid", "rgba(45,20,43,0.1)"),
    boxShadow: "0 -6px 18px rgba(45,20,43,0.06)",
  },
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    columnGap: "12px",
    flexWrap: "wrap",
  },
  pageTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: BRAND.ink,
    margin: 0,
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
  },
  titleStack: { display: "flex", flexDirection: "column", rowGap: "6px", minWidth: 0 },
  titleChips: { display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" },
  titleChip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "10px",
    paddingRight: "10px",
    backgroundColor: tokens.colorNeutralBackground2,
    color: BRAND.ink,
    fontSize: "0.78rem",
    fontWeight: 600,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("999px"),
    lineHeight: 1.2,
  },
  titleChipIcon: { display: "inline-flex", alignItems: "center", color: BRAND.muted },
  contextPanel: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "18px",
    paddingRight: "18px",
    display: "flex",
    flexDirection: "row",
    columnGap: "16px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  contextBlock: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
    minWidth: "180px",
  },
  contextLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  contextName: { fontFamily: BRAND.fontAccent, fontSize: "1.2rem", color: BRAND.ink },
  contextSub: { fontSize: "0.85rem", color: BRAND.muted },
  formPanel: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "22px",
    paddingRight: "22px",
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  sectionPill: {
    display: "inline-block",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "10px",
    paddingRight: "10px",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.white,
    alignSelf: "flex-start",
    ...shorthands.borderRadius("999px"),
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "16px",
    flexWrap: "wrap",
    marginTop: "4px",
  },
  heroBox: { flexShrink: 0 },
  title: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.6rem, 4vw, 2.4rem)",
    fontWeight: 400,
    color: BRAND.ink,
    lineHeight: 1.15,
    flexGrow: 1,
    minWidth: "200px",
  },
  description: {
    marginTop: "0",
    marginBottom: 0,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.muted,
  },
  questions: { marginTop: "12px" },
  sectionTabs: { display: "flex", flexWrap: "wrap", columnGap: "8px", rowGap: "8px" },
  sectionTab: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: tokens.colorNeutralBackground1,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 600,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    transitionProperty: "background-color, border-color, color",
    transitionDuration: "120ms",
    ":hover": { ...shorthands.borderColor(BRAND.purple) },
  },
  sectionTabActive: { color: BRAND.white },
  blocker: {
    backgroundColor: "#fff5e6",
    color: "#7a4a10",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    fontSize: "0.88rem",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", "#f3d4a3"),
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "12px",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("8px"),
  },
  searchInput: {
    flex: 1,
    backgroundColor: "transparent",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    outlineWidth: 0,
    border: "none",
  },
  betriebRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    backgroundColor: BRAND.bg,
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
  },
  riskGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "10px",
    maxHeight: "320px",
    overflowY: "auto",
    paddingRight: "4px",
    marginTop: "10px",
  },
  riskItem: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      backgroundColor: "#f5eef6",
    },
  },
  riskItemActive: {
    ...shorthands.borderColor(BRAND.purple),
    backgroundColor: "#f5eef6",
    boxShadow: `inset 0 0 0 1px ${BRAND.purple}`,
  },
  riskBadge: {
    width: "28px",
    height: "28px",
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    flexShrink: 0,
    fontSize: "0.78rem",
    ...shorthands.borderRadius("999px"),
  },
  riskBadgeChecked: { backgroundColor: "#0e7c3a" },
  riskBody: { display: "flex", flexDirection: "column", minWidth: 0, rowGap: "2px" },
  riskName: { fontWeight: 600, color: BRAND.ink, fontSize: "0.92rem" },
  riskSub: { fontSize: "0.78rem", color: BRAND.muted },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "8px",
  },
  summaryRow: {
    display: "grid",
    gridTemplateColumns: "1fr",
    columnGap: "16px",
    rowGap: "2px",
    paddingTop: "6px",
    paddingBottom: "6px",
    ...shorthands.borderBottom("1px", "dashed", BRAND.borderDashed),
    "@media (min-width: 720px)": { gridTemplateColumns: "minmax(220px, 0.8fr) 1.2fr" },
  },
  summaryKey: { fontSize: "0.8rem", fontWeight: 600, color: BRAND.muted },
  summaryValue: { fontSize: "0.95rem", color: BRAND.ink, whiteSpace: "pre-wrap" },
});

type Answers = {
  contact: ContactSummary | null;
  vorvertrag: VorvertragInfo;
  versicherer: VersichererKey;
  vertragsart: VertragsartKey | "";
  tarifierung: TarifierungKey;
  selectedRiskId: number | null;
  summen: SummenInfo;
  umfang: UmfangInfo;
  /** Gefahren-Matrix für Inhaltsversicherung & Ertragsausfallversicherung. */
  gefahrenMatrix: GefahrenMatrixState;
  zahlung: ZahlungInfo;
};

const initialAnswers = (): Answers => ({
  contact: null,
  vorvertrag: { exists: "" },
  versicherer: "",
  vertragsart: "",
  tarifierung: "",
  selectedRiskId: null,
  summen: { versicherungssumme: "" },
  umfang: { selbstbeteiligung: "" },
  gefahrenMatrix: emptyMatrixState(),
  zahlung: {
    jahrespraemieBrutto: "",
    jahrespraemieNetto: "",
    versicherungssteuersatz: "",
    praemieJeZahlweise: "",
    versicherungsbeginn: "",
    versicherungsablauf: "",
    hauptfaelligkeit: "",
    naechsteFaelligkeit: "",
    zahlweise: "",
    zahlungsart: "",
    bankverbindung: "",
    rechnungsempfaenger: "",
    abweichenderRechnungsempfaenger: "",
    bemerkung: "",
    vertragsnummer: "",
    versicherungsnummer: "",
    zweig: "",
    risikoOrt: "",
  },
});

const initials = (name: string) =>
  name
    .split(/\s|,/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase() || "?";

const formatAddr = (c: ContactSummary | null) => {
  if (!c) return "";
  const parts = [
    c.street,
    [c.postalCode, c.city].filter(Boolean).join(" "),
    c.country,
  ].filter(Boolean) as string[];
  return parts.join(", ");
};

const buildDisplay = (c: any, fallbackId: number): string => {
  if (!c) return `Kontakt ${fallbackId}`;
  if (typeof c.displayName === "string" && c.displayName.trim()) return c.displayName;
  const first = c.firstName ?? "";
  const last = c.lastname ?? c.lastName ?? "";
  if (last && first) return `${last}, ${first}`;
  if (last) return String(last);
  if (first) return String(first);
  if (c.companyName) return String(c.companyName);
  return `Kontakt ${fallbackId}`;
};

const buildContactSummary = (c: any, fallbackId: number, role?: string): ContactSummary => ({
  id: c?.id ?? fallbackId,
  displayName: buildDisplay(c, fallbackId),
  role,
  street: c?.defaultAddress?.street ?? c?.address?.street,
  postalCode: c?.defaultAddress?.postalCode ?? c?.address?.postalCode,
  city: c?.defaultAddress?.city ?? c?.address?.city,
  country: c?.defaultAddress?.country ?? c?.address?.country,
});

const AllgemeinContractCreatePage = () => {
  const styles = useStyles();
  const shared = useSharedStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const toasterId = "allgemein-contract-toaster";
  const { dispatchToast } = useToastController(toasterId);

  const initialKontaktId = useMemo<number | null>(() => {
    const fromState = Number((location.state as { kontaktId?: number } | null)?.kontaktId ?? 0);
    if (Number.isFinite(fromState) && fromState > 0) return fromState;
    const fromQuery = Number(searchParams.get("contactId") ?? 0);
    return Number.isFinite(fromQuery) && fromQuery > 0 ? fromQuery : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const artName = useMemo<string | null>(() => {
    const v = (location.state as { artName?: string | null } | null)?.artName;
    return typeof v === "string" && v.trim() ? v : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const matrixMode = isMatrixMode(artName);
  const inhaltMode = isInhaltOnly(artName);

  const [answers, setAnswers] = useState<Answers>(initialAnswers());
  const [risks, setRisks] = useState<RiskOption[]>([]);
  const [riskSearch, setRiskSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
  const [currentSectionIdx, setCurrentSectionIdx] = useState(0);
  const [currentSubStepIdx, setCurrentSubStepIdx] = useState(0);

  const sections = ALLGEMEIN_CONTRACT_SECTIONS;
  const draftKey = useMemo(
    () => `${DRAFT_LS_PREFIX}${initialKontaktId ?? "new"}`,
    [initialKontaktId],
  );

  // Load preselected contact, if any
  useEffect(() => {
    if (!initialKontaktId) return;
    let aborted = false;
    (async () => {
      try {
        const res = await fetch(apiUrl(`contacts/${initialKontaktId}`));
        if (!res.ok) return;
        const data = await res.json();
        if (aborted) return;
        const summary = buildContactSummary(data, initialKontaktId, "Versicherungsnehmer");
        setAnswers((p) => (p.contact ? p : { ...p, contact: summary }));
      } catch (err) {
        console.error("Kontakt laden fehlgeschlagen", err);
      }
    })();
    return () => {
      aborted = true;
    };
  }, [initialKontaktId]);

  // Load risks for the chosen contact
  useEffect(() => {
    const cid = answers.contact?.id;
    if (!cid) {
      setRisks([]);
      return;
    }
    let aborted = false;
    (async () => {
      try {
        const res = await fetch(apiUrl(`risk-objects/by-contact/${cid}`));
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const mapped: RiskOption[] = (Array.isArray(data) ? data : [])
          .map((it: any) => ({
            id: Number(it.id),
            name: String(it.name ?? "").trim() || `Risiko ${it.id}`,
            riskType: String(it.riskType ?? "") || undefined,
            contactID: Number(it.contactID ?? 0),
            contractID: Number(it.contractID ?? 0),
          }))
          .filter((r: RiskOption) => Number.isFinite(r.id) && r.id > 0);
        if (!aborted) setRisks(mapped);
      } catch (err) {
        console.error("Risikoobjekte laden fehlgeschlagen", err);
        if (!aborted) setRisks([]);
      }
    })();
    return () => {
      aborted = true;
    };
  }, [answers.contact?.id]);

  // Restore draft once
  const draftLoadedRef = useRef(false);
  useEffect(() => {
    if (draftLoadedRef.current) return;
    draftLoadedRef.current = true;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && parsed.answers) {
        setAnswers((prev) => ({ ...prev, ...parsed.answers }));
        const totalSecs = sections.length;
        const sec = Math.min(Math.max(0, parsed.currentSectionIdx ?? 0), totalSecs - 1);
        const maxStep = (sections[sec]?.steps.length ?? 1) - 1;
        const step = Math.min(Math.max(0, parsed.currentSubStepIdx ?? 0), maxStep);
        setCurrentSectionIdx(sec);
        setCurrentSubStepIdx(step);
        setDraftSavedAt(parsed.savedAt ?? null);
      }
    } catch {
      // ignore
    }
  }, [draftKey, sections]);

  // Autosave
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      const savedAt = new Date().toISOString();
      try {
        localStorage.setItem(
          draftKey,
          JSON.stringify({ answers, currentSectionIdx, currentSubStepIdx, savedAt }),
        );
        setDraftSavedAt(savedAt);
      } catch {
        // ignore
      }
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [answers, currentSectionIdx, currentSubStepIdx, draftKey]);

  const totalSteps = useMemo(
    () => sections.reduce((acc, s) => acc + s.steps.length, 0),
    [sections],
  );
  const stepIndex = useMemo(() => {
    let idx = 0;
    for (let s = 0; s < currentSectionIdx; s++) idx += sections[s].steps.length;
    return idx + currentSubStepIdx;
  }, [sections, currentSectionIdx, currentSubStepIdx]);
  const progressFraction = totalSteps > 0 ? stepIndex / Math.max(1, totalSteps - 1) : 0;

  const submitSectionIdx = sections.length - 1;
  const isSubmitStep =
    currentSectionIdx === submitSectionIdx &&
    currentSubStepIdx === (sections[submitSectionIdx]?.steps.length ?? 1) - 1;

  const goPrev = () => {
    if (currentSubStepIdx > 0) {
      setCurrentSubStepIdx(currentSubStepIdx - 1);
    } else if (currentSectionIdx > 0) {
      const prev = currentSectionIdx - 1;
      setCurrentSectionIdx(prev);
      setCurrentSubStepIdx(sections[prev].steps.length - 1);
    }
  };
  const goNext = () => {
    const sec = sections[currentSectionIdx];
    if (currentSubStepIdx < sec.steps.length - 1) {
      setCurrentSubStepIdx(currentSubStepIdx + 1);
      return;
    }
    if (currentSectionIdx + 1 >= sections.length) return;
    setCurrentSectionIdx(currentSectionIdx + 1);
    setCurrentSubStepIdx(0);
  };

  const saveDraftNow = () => {
    setSavingDraft(true);
    const savedAt = new Date().toISOString();
    try {
      localStorage.setItem(
        draftKey,
        JSON.stringify({ answers, currentSectionIdx, currentSubStepIdx, savedAt }),
      );
      setDraftSavedAt(savedAt);
    } catch {
      // ignore
    } finally {
      setTimeout(() => setSavingDraft(false), 250);
    }
  };

  const stepBlockerMessage = (kind: ContractStepKind): string | null => {
    const a = answers;
    if (kind !== "kontakt" && !a.contact) return "Bitte zuerst den Versicherungsnehmer auswählen.";
    if (
      (kind === "risikoinformation" ||
        kind === "summen" ||
        kind === "umfang" ||
        kind === "zahlung" ||
        kind === "summary") &&
      !a.vertragsart
    ) {
      return "Bitte zuerst die Vertragsart wählen.";
    }
    if (
      requiresTarifierung(a.vertragsart) &&
      !a.tarifierung &&
      (kind === "risikoinformation" ||
        kind === "summen" ||
        kind === "umfang" ||
        kind === "zahlung" ||
        kind === "summary")
    ) {
      return "Bitte das Produkt (Tarifierung) wählen.";
    }
    return null;
  };

  const filteredRisks = useMemo(() => {
    const q = riskSearch.trim().toLowerCase();
    if (!q) return risks;
    return risks.filter(
      (r) => r.name.toLowerCase().includes(q) || (r.riskType ?? "").toLowerCase().includes(q),
    );
  }, [risks, riskSearch]);

  const onSubmit = async () => {
    const a = answers;
    if (!a.contact) {
      dispatchToast(
        <Toast>
          <ToastTitle>Bitte einen Kontakt auswählen</ToastTitle>
        </Toast>,
        { intent: "warning" },
      );
      return;
    }
    setSubmitting(true);
    try {
      const characteristics: Array<{ nameBezeichnung: string; contractCharacteristicValue: string }> = [];
      const push = (name: string, value: string | null | undefined) => {
        if (value === null || value === undefined || value === "") return;
        characteristics.push({ nameBezeichnung: name, contractCharacteristicValue: String(value) });
      };
      push("allg.vorvertrag_exists", a.vorvertrag.exists);
      push("allg.vorvertrag_gesellschaft", a.vorvertrag.gesellschaft);
      push("allg.vorvertrag_nummer", a.vorvertrag.vertragsnummer);
      push("allg.vorvertrag_kuendigung_zum", a.vorvertrag.kuendigungZum);
      push("allg.versicherer", a.versicherer);
      push("allg.vertragsart", a.vertragsart);
      push("allg.tarifierung", a.tarifierung);
      push("allg.versicherungssumme", a.summen.versicherungssumme);
      push("allg.selbstbeteiligung", a.umfang.selbstbeteiligung);
      if (artName) push("allg.artName", artName);
      if (matrixMode) {
        // Nur tatsächlich versicherte Zeilen in den Payload schreiben.
        const reduced = Object.fromEntries(
          Object.entries(a.gefahrenMatrix).filter(([, row]) => row.versichert),
        );
        push("allg.gefahren_matrix_json", JSON.stringify(reduced));
        push("allg.gefahren_mode", inhaltMode ? "inhalt" : "ertragsausfall");
      }
      push("allg.jahrespraemie_brutto", a.zahlung.jahrespraemieBrutto);
      push("allg.jahrespraemie_netto", a.zahlung.jahrespraemieNetto);
      push("allg.versicherungssteuersatz", a.zahlung.versicherungssteuersatz);
      push("allg.praemie_je_zahlweise", a.zahlung.praemieJeZahlweise);
      push("allg.versicherungsbeginn", a.zahlung.versicherungsbeginn);
      push("allg.versicherungsablauf", a.zahlung.versicherungsablauf);
      push("allg.naechste_faelligkeit", a.zahlung.naechsteFaelligkeit);
      push("allg.zahlungsart", a.zahlung.zahlungsart);
      push("allg.bankverbindung", a.zahlung.bankverbindung);
      push("allg.rechnungsempfaenger", a.zahlung.rechnungsempfaenger);
      push("allg.abw_rechnungsempfaenger", a.zahlung.abweichenderRechnungsempfaenger);

      const jahresPraemieNum =
        a.zahlung.jahrespraemieBrutto.trim() !== "" &&
        Number.isFinite(Number(a.zahlung.jahrespraemieBrutto))
          ? Number(a.zahlung.jahrespraemieBrutto)
          : null;

      const payload: Record<string, any> = {
        contractNbr: a.zahlung.vertragsnummer.trim() || `ALLG-${Date.now()}`,
        insuranceNbr: a.zahlung.versicherungsnummer.trim() || "unbekannt",
        branch: a.zahlung.zweig || "unbekannt",
        riskLocation:
          a.zahlung.risikoOrt.trim() ||
          risks.find((r) => r.id === a.selectedRiskId)?.name ||
          "unbekannt",
        mainDueDate: a.zahlung.hauptfaelligkeit || null,
        paymentFrequency: a.zahlung.zahlweise || null,
        annualFee: jahresPraemieNum,
        remark: a.zahlung.bemerkung.trim() || null,
        contact: { id: a.contact.id },
        characteristics,
      };

      const res = await fetch(apiUrl("contracts"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        console.error("Contract POST failed", res.status, text);
        dispatchToast(
          <Toast>
            <ToastTitle>Speichern fehlgeschlagen</ToastTitle>
          </Toast>,
          { intent: "error" },
        );
        return;
      }
      let createdId: number | null = null;
      try {
        const created = await res.json();
        createdId = created?.id ?? null;
      } catch {
        // ignore
      }
      if (a.selectedRiskId && createdId) {
        try {
          await fetch(apiUrl(`risk-objects/${a.selectedRiskId}`), {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ contractID: createdId, contactID: a.contact.id }),
          });
        } catch {
          // ignore link-failure (vertrag wurde angelegt)
        }
      }
      try {
        localStorage.removeItem(draftKey);
      } catch {
        // ignore
      }
      dispatchToast(
        <Toast>
          <ToastTitle>Vertrag erstellt</ToastTitle>
        </Toast>,
        { intent: "success" },
      );
      if (createdId) {
        navigate(`/vertraege/edit/${createdId}`);
      } else if (a.contact?.id) {
        navigate(`/contacts/${a.contact.id}`, { state: { defaultTab: "vertraege" } });
      } else {
        navigate("/vertraege");
      }
    } catch (e) {
      console.error("Contract POST threw", e);
      dispatchToast(
        <Toast>
          <ToastTitle>Speichern fehlgeschlagen</ToastTitle>
        </Toast>,
        { intent: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Step renderer
  const section = sections[currentSectionIdx] ?? sections[0];
  const step = section.steps[currentSubStepIdx] ?? section.steps[0];
  const accent = sectionColorHex[section.color];

  const tarifLabel = TARIFIERUNGEN.find((t) => t.key === answers.tarifierung)?.label ?? "—";
  const vertragsartLabel = VERTRAGSARTEN.find((v) => v.key === answers.vertragsart)?.label ?? "—";
  const versichererLabel = VERSICHERER_OPTIONS.find((v) => v.key === answers.versicherer)?.label ?? "—";

  const renderStep = (kind: ContractStepKind) => {
    switch (kind) {
      case "kontakt":
        return (
          <div style={{ display: "flex", flexDirection: "column", rowGap: 16 }}>
            <ContactSelector
              label="Versicherungsnehmer"
              placeholder="Kontakt suchen (Name, Firma)…"
              selectedContact={answers.contact}
              onSelect={(c) => {
                if (!c) return;
                const summary = buildContactSummary(c, c.id ?? 0, "Versicherungsnehmer");
                setAnswers((p) => {
                  const sameContact = p.contact?.id === summary.id;
                  return sameContact
                    ? { ...p, contact: summary }
                    : { ...p, contact: summary, selectedRiskId: null };
                });
              }}
            />
            {answers.contact && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: 4,
                  padding: "12px 14px",
                  backgroundColor: BRAND.bg,
                  borderRadius: 8,
                  border: `1px solid ${BRAND.borderDashed}`,
                }}
              >
                <span style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: BRAND.muted }}>
                  Ausgewählter Versicherungsnehmer
                </span>
                <span style={{ fontFamily: BRAND.fontAccent, fontSize: "1.15rem", color: BRAND.ink }}>
                  {answers.contact.displayName}
                </span>
                <span style={{ fontSize: "0.85rem", color: BRAND.muted }}>
                  {formatAddr(answers.contact) || "—"}
                </span>
              </div>
            )}
          </div>
        );
      case "vertragsBasis":
        return (
          <VertragsBasisSection
            vorvertrag={answers.vorvertrag}
            versicherer={answers.versicherer}
            vertragsart={answers.vertragsart}
            tarifierung={answers.tarifierung}
            onVorvertragChange={(v) => setAnswers((p) => ({ ...p, vorvertrag: v }))}
            onVersichererChange={(v) => setAnswers((p) => ({ ...p, versicherer: v }))}
            onVertragsartChange={(v) =>
              setAnswers((p) => ({
                ...p,
                vertragsart: v,
                // Tarifierung nur für Einzelvertrag — sonst zurücksetzen.
                tarifierung: requiresTarifierung(v) ? p.tarifierung : "",
              }))
            }
            onTarifierungChange={(v) => setAnswers((p) => ({ ...p, tarifierung: v }))}
          />
        );
      case "risikoinformation":
        // Bei Inhaltsversicherung & Ertragsausfallversicherung ist die Betriebsanschrift
        // selbst das Risiko — kein separater Risikoobjekt-Picker nötig.
        if (matrixMode) {
          return (
            <div style={{ display: "flex", flexDirection: "column", rowGap: 14 }}>
              <p className={shared.helper}>
                Für{" "}
                <strong>
                  {inhaltMode ? "Inhaltsversicherungen" : "Ertragsausfallversicherungen"}
                </strong>{" "}
                gilt die Betriebsanschrift des Versicherungsnehmers als versichertes Risiko.
              </p>
              {answers.contact ? (
                <div className={styles.betriebRow}>
                  <Building20Regular />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span
                      style={{
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        color: BRAND.muted,
                      }}
                    >
                      Risiko / Betriebsanschrift
                    </span>
                    <span style={{ fontSize: "1.05rem", color: BRAND.ink, fontWeight: 600 }}>
                      {answers.contact.displayName}
                    </span>
                    <span style={{ fontSize: "0.9rem", color: BRAND.muted }}>
                      {formatAddr(answers.contact) || "—"}
                    </span>
                  </div>
                </div>
              ) : (
                <div className={styles.blocker}>
                  Bitte zuerst den Versicherungsnehmer im Schritt „Kontakt" auswählen.
                </div>
              )}
            </div>
          );
        }
        return (
          <div style={{ display: "flex", flexDirection: "column", rowGap: 14 }}>
            {answers.contact && (
              <div className={styles.betriebRow}>
                <Building20Regular />
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: BRAND.muted }}>
                    Betriebsanschrift
                  </span>
                  <span style={{ fontSize: "0.95rem", color: BRAND.ink }}>
                    {formatAddr(answers.contact) || "—"}
                  </span>
                </div>
              </div>
            )}
            <div className={styles.searchRow}>
              <Search20Regular />
              <input
                className={styles.searchInput}
                type="search"
                placeholder="Risiko suchen (z.B. Cyber, Haftpflicht, Name)…"
                value={riskSearch}
                onChange={(e) => setRiskSearch(e.target.value)}
              />
            </div>
            {filteredRisks.length === 0 ? (
              <div className={styles.blocker}>
                Keine Risikoobjekte zu diesem Kontakt vorhanden — bitte zuerst ein Risiko anlegen.
              </div>
            ) : (
              <div className={styles.riskGrid}>
                {filteredRisks.map((r) => {
                  const active = answers.selectedRiskId === r.id;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      className={mergeClasses(styles.riskItem, active && styles.riskItemActive)}
                      onClick={() =>
                        setAnswers((p) => ({ ...p, selectedRiskId: r.id }))
                      }
                    >
                      <span
                        className={mergeClasses(styles.riskBadge, active && styles.riskBadgeChecked)}
                      >
                        {active ? <Checkmark16Regular /> : initials(r.name)}
                      </span>
                      <span className={styles.riskBody}>
                        <span className={styles.riskName}>{r.name}</span>
                        {r.riskType && <span className={styles.riskSub}>{r.riskType}</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      case "summen":
        return (
          <div className={shared.cardSection}>
            <p className={shared.helper}>
              Die Summe, bis zu der der Versicherer im Schadenfall haftet.
            </p>
            <div className={shared.fieldGrid}>
              <div className={shared.field}>
                <label className={shared.label}>Versicherungssumme (€)</label>
                <input
                  className={shared.input}
                  value={answers.summen.versicherungssumme}
                  inputMode="decimal"
                  onChange={(e) =>
                    setAnswers((p) => ({
                      ...p,
                      summen: { ...p.summen, versicherungssumme: e.target.value },
                    }))
                  }
                  placeholder="0,00"
                />
              </div>
            </div>
          </div>
        );
      case "umfang":
        if (matrixMode) {
          return (
            <GefahrenMatrix
              value={answers.gefahrenMatrix}
              onChange={(next) => setAnswers((p) => ({ ...p, gefahrenMatrix: next }))}
              showErtragsausfall={inhaltMode}
            />
          );
        }
        return (
          <div className={shared.cardSection}>
            <p className={shared.helper}>
              Die Selbstbeteiligung, die der Versicherungsnehmer im Schadenfall trägt.
            </p>
            <div className={shared.fieldGrid}>
              <div className={shared.field}>
                <label className={shared.label}>Selbstbeteiligung (€)</label>
                <input
                  className={shared.input}
                  value={answers.umfang.selbstbeteiligung}
                  inputMode="decimal"
                  onChange={(e) =>
                    setAnswers((p) => ({
                      ...p,
                      umfang: { ...p.umfang, selbstbeteiligung: e.target.value },
                    }))
                  }
                  placeholder="0,00"
                />
              </div>
            </div>
          </div>
        );
      case "zahlung":
        return (
          <ZahlungSection
            value={answers.zahlung}
            onChange={(patch) =>
              setAnswers((p) => ({ ...p, zahlung: { ...p.zahlung, ...patch } }))
            }
          />
        );
      case "summary": {
        const rows: Array<{ key: string; value: string }> = [
          { key: "Versicherungsnehmer", value: answers.contact?.displayName ?? "—" },
          { key: "Betriebsanschrift", value: formatAddr(answers.contact) || "—" },
          {
            key: "Vorvertrag",
            value:
              answers.vorvertrag.exists === "ja"
                ? `Ja — ${answers.vorvertrag.gesellschaft || "—"} · ${answers.vorvertrag.vertragsnummer || "—"}`
                : answers.vorvertrag.exists === "nein"
                  ? "Nein (Neuvertrag)"
                  : "—",
          },
          { key: "Vertragsart", value: vertragsartLabel },
          { key: "Versicherer", value: versichererLabel },
          { key: "Produkt / Tarifierung", value: requiresTarifierung(answers.vertragsart) ? tarifLabel : "—" },
          {
            key: "Risiko",
            value:
              risks.find((r) => r.id === answers.selectedRiskId)?.name ?? "—",
          },
          { key: "Versicherungssumme", value: answers.summen.versicherungssumme ? `${answers.summen.versicherungssumme} €` : "—" },
          { key: "Selbstbeteiligung", value: answers.umfang.selbstbeteiligung ? `${answers.umfang.selbstbeteiligung} €` : "—" },
          { key: "Zweig", value: answers.zahlung.zweig || "—" },
          { key: "Zahlweise", value: answers.zahlung.zahlweise || "—" },
          {
            key: "Jahresprämie (Brutto)",
            value: answers.zahlung.jahrespraemieBrutto
              ? `${answers.zahlung.jahrespraemieBrutto} €`
              : "—",
          },
        ];
        return (
          <div className={styles.summaryGrid}>
            {rows.map((r) => (
              <div key={r.key} className={styles.summaryRow}>
                <span className={styles.summaryKey}>{r.key}</span>
                <span className={styles.summaryValue}>{r.value || "—"}</span>
              </div>
            ))}
          </div>
        );
      }
      default:
        return null;
    }
  };

  const stepBlocker = stepBlockerMessage(step.kind);

  return (
    <div className={styles.page}>
      <Toaster toasterId={toasterId} />
      <div className={styles.stickyHeader}>
        <div className={styles.pageHeader}>
          <div className={styles.titleStack}>
            <h1 className={styles.pageTitle}>
              <span className={styles.titleChipIcon} aria-hidden="true">
                <DocumentAdd20Regular />
              </span>
              Vertrag anlegen
            </h1>
            <div className={styles.titleChips}>
              <span className={styles.titleChip}>
                <span className={styles.titleChipIcon} aria-hidden="true">
                  <Briefcase20Regular />
                </span>
                {artName ?? "Allgemeiner Vertrag"}
              </span>
              {answers.vertragsart && (
                <span className={styles.titleChip}>
                  <span className={styles.titleChipIcon} aria-hidden="true">
                    <DocumentBulletList20Regular />
                  </span>
                  {vertragsartLabel}
                </span>
              )}
              {answers.versicherer && (
                <span className={styles.titleChip}>{versichererLabel}</span>
              )}
              {requiresTarifierung(answers.vertragsart) && answers.tarifierung && (
                <span className={styles.titleChip}>{tarifLabel}</span>
              )}
            </div>
          </div>
          <Button appearance="subtle" onClick={() => navigate(-1)}>
            Zurück
          </Button>
        </div>

        <div className={styles.contextPanel}>
          <div className={styles.contextBlock}>
            <span className={styles.contextLabel}>Kontakt</span>
            <span className={styles.contextName}>{answers.contact?.displayName ?? "—"}</span>
            <span className={styles.contextSub}>{formatAddr(answers.contact) || "—"}</span>
          </div>
          <div className={styles.contextBlock}>
            <span className={styles.contextLabel}>Risiko</span>
            <span className={styles.contextSub}>
              {risks.find((r) => r.id === answers.selectedRiskId)?.name ?? "Keines ausgewählt"}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.sectionTabs} role="tablist" aria-label="Abschnitte">
        {sections.map((s, sIdx) => {
          const active = sIdx === currentSectionIdx;
          const color = sectionColorHex[s.color];
          return (
            <button
              key={s.key}
              type="button"
              role="tab"
              aria-selected={active}
              className={mergeClasses(styles.sectionTab, active && styles.sectionTabActive)}
              style={
                active
                  ? { backgroundColor: color, borderColor: color }
                  : { color }
              }
              onClick={() => {
                setCurrentSectionIdx(sIdx);
                setCurrentSubStepIdx(0);
              }}
            >
              {sIdx + 1}. {s.title}
            </button>
          );
        })}
      </div>

      <div className={styles.formPanel}>
        <span className={styles.sectionPill} style={{ backgroundColor: accent }}>
          {section.title}
        </span>
        <div className={styles.titleRow}>
          <div style={{ display: "flex", flexDirection: "column", flexGrow: 1, minWidth: 200 }}>
            <h2 className={styles.title}>{step.title}</h2>
            {step.description && <p className={styles.description}>{step.description}</p>}
          </div>
          <div className={styles.heroBox}>
            <StepHero kind={step.kind} />
          </div>
        </div>
        <div className={styles.questions}>
          {stepBlocker ? <div className={styles.blocker}>{stepBlocker}</div> : renderStep(step.kind)}
        </div>
      </div>

      <div className={styles.stickyFooter}>
        <BottomBar
          onPrev={goPrev}
          onNext={goNext}
          onSubmit={onSubmit}
          onSaveDraft={saveDraftNow}
          canPrev={!(currentSectionIdx === 0 && currentSubStepIdx === 0)}
          canNext={!stepBlocker}
          nextDisabledTitle={stepBlocker ?? undefined}
          isLastStep={isSubmitStep}
          submitting={submitting}
          savingDraft={savingDraft}
          draftSavedAt={draftSavedAt}
          progressLabel={`Schritt ${stepIndex + 1} von ${totalSteps} · ${section.title}`}
          progressFraction={progressFraction}
          submitLabel="Vertrag anlegen"
          submittingLabel="Wird angelegt…"
        />
      </div>
    </div>
  );
};

export default AllgemeinContractCreatePage;
